import { useMemo } from "react";
import type { ChannelMember, ChatMessage } from "@/api/connectApi";
import { useAuth } from "react-oidc-context";
import { normalizeUserId } from "@/api/connectApi";

/**
 * Teams-style "Seen by" footer. For each channel member, finds the
 * latest message whose <c>sentAt</c> is &lt;= their <c>lastReadAt</c>;
 * the resulting (message → members) map drives a small avatar strip
 * rendered under that message. The strip never shows the local user
 * (you've trivially read your own messages) and caps at 4 avatars +
 * an "+N" overflow chip so a 50-member channel stays readable.
 */
export function ReadReceiptAvatars({
    message,
    receiptsByMessage
}: {
    message: ChatMessage;
    receiptsByMessage: Map<string, ChannelMember[]>;
}) {
    const seenBy = receiptsByMessage.get(message.id);
    if (!seenBy || seenBy.length === 0) return null;
    const max = 4;
    const visible = seenBy.slice(0, max);
    const overflow = seenBy.length - visible.length;
    return (
        <div
            className="mt-1 flex items-center gap-0.5"
            aria-label={`Seen by ${seenBy.length} member${seenBy.length === 1 ? "" : "s"}`}
            title={seenBy.map((m) => m.displayName ?? m.userEmail).join(", ")}
        >
            <span className="mr-1 text-[9px] uppercase tracking-wider text-muted-foreground">Seen by</span>
            {visible.map((m) => (
                <span
                    key={m.id}
                    className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-background bg-muted text-[8px] font-semibold leading-none"
                >
                    {avatarInitials(m.displayName ?? m.userEmail)}
                </span>
            ))}
            {overflow > 0 ? (
                <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full border border-background bg-primary/10 px-1 text-[8px] font-semibold text-primary">
                    +{overflow}
                </span>
            ) : null}
        </div>
    );
}

/**
 * Pre-compute, per render, the (latest-message-each-member-has-seen)
 * map. O(messages × members) but both lists are usually small (~50
 * messages on screen, ~10 members in the average channel) so a single
 * useMemo is plenty.
 */
export function useReadReceiptMap(
    members: ChannelMember[],
    messages: ChatMessage[]
): Map<string, ChannelMember[]> {
    const auth = useAuth();
    const myUserId = normalizeUserId(auth.user?.profile.sub);
    return useMemo(() => {
        const map = new Map<string, ChannelMember[]>();
        if (!messages.length || !members.length) return map;
        // Sort once; assume `messages` arrives newest-last (the channel
        // view appends in order) but defend with a sort just in case.
        const sortedAsc = [...messages].sort(
            (a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime()
        );
        for (const member of members) {
            if (!member.lastReadAt) continue;
            // Skip self — your own avatar under your own messages is noise.
            if (normalizeUserId(member.userId) === myUserId) continue;
            const lastReadMs = new Date(member.lastReadAt).getTime();
            // Walk newest-first, find the most recent message they've
            // read. Bail out as soon as we find one.
            for (let i = sortedAsc.length - 1; i >= 0; i--) {
                const m = sortedAsc[i]!;
                if (new Date(m.sentAt).getTime() <= lastReadMs) {
                    const list = map.get(m.id);
                    if (list) list.push(member);
                    else map.set(m.id, [member]);
                    break;
                }
            }
        }
        return map;
    }, [members, messages, myUserId]);
}

function avatarInitials(s: string): string {
    const parts = s.split(/[\s@]+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0]!.charAt(0).toUpperCase();
    return (parts[0]!.charAt(0) + parts[1]!.charAt(0)).toUpperCase();
}
